// REPORTS-UX-1 — Report Registry.
//
// The single declarative source describing every report as a first-class,
// navigable workspace. Each entry owns its identity, category, capability gate,
// the PRIMARY view-mode (the discriminator that makes "WIP — Team Rollup" a
// distinct pin from "WIP — Claims I Lead"), a navLabel, and the two pure
// mappers serialize(stateBag) -> viewState and applyParams(viewState, setters).
//
// A "view state" is the MINIMAL serializable description of a report's config:
// modes + filters + sort + date range. It MUST exclude result rows, facets, and
// transient UI (expanded ids, busy flags) — that data is re-fetched, never pinned.
//
// How it participates in Global Nav: Reports.js builds the live viewState each
// render via REPORTS[activeReport].serialize(localStateBag) and feeds it to
// useReportNav, which writes {report, view, label} into the current nav location
// in place. Pins/recents/saved-views then capture and restore the exact view.
import { can } from "../api";

export const REPORT_CATEGORY = {
  OPERATIONAL: "Operational",
  FINANCIAL: "Financial",
  MANAGEMENT: "Management",
  MARKET: "Market / Regulatory",
};

const WIP_MODE_LABEL = { worked: "My Worked", leading: "Claims I Lead", rollup: "Team Rollup" };
const PROD_MODE_LABEL = { self: "Me", one: "Adjuster", all: "All Staff" };
const cap1 = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);

export const REPORTS = {
  // ── Operational ──────────────────────────────────────────────────────────
  "adjuster-wip": {
    id: "adjuster-wip",
    label: "Work in Progress (WIP)",
    category: REPORT_CATEGORY.OPERATIONAL,
    description: "Unbilled time & expense readiness across your claims; trigger invoicing.",
    capability: null,
    modeCapability: { rollup: "view_team_reports" }, // Team Rollup needs team-reports
    pinnable: true, savesViews: true, exportable: false,
    primaryModeKey: "wipMode",
    defaultViewState: { wipMode: "worked", statusFilter: "Open", viewAsUserId: null,
                        dateFrom: "", dateTo: "", sort: { key: "wip_age_days", dir: "desc" } },
    navLabel: (vs) => `WIP — ${WIP_MODE_LABEL[vs?.wipMode] || "My Worked"}`,
    serialize: (s) => ({ wipMode: s.wipMode, statusFilter: s.statusFilter,
      viewAsUserId: s.viewAsUserId ?? null, dateFrom: s.dateFrom, dateTo: s.dateTo, sort: s.sort }),
    applyParams: (p, set) => {
      if (p.wipMode != null) set.setWipMode(p.wipMode);
      if (p.statusFilter != null) set.setStatusFilter(p.statusFilter);
      if (p.viewAsUserId !== undefined) set.setViewAsUserId(p.viewAsUserId);
      if (p.dateFrom != null) set.setDateFrom(p.dateFrom);
      if (p.dateTo != null) set.setDateTo(p.dateTo);
      if (p.sort) set.setSort(p.sort);
    },
  },

  "reimbursement-requests": {
    id: "reimbursement-requests",
    label: "Funds Requests",
    category: REPORT_CATEGORY.OPERATIONAL,
    description: "Cross-claim reimbursement / funding requests and deposits.",
    capability: null,
    pinnable: true, savesViews: true, exportable: false,
    primaryModeKey: "status",
    selfContained: true, // state lives in ReimbursementRequestsReport
    defaultViewState: { status: "outstanding", q: "", sort: { key: "age_days", dir: "desc" } },
    navLabel: (vs) => `Funds Requests — ${cap1(vs?.status) || "Outstanding"}`,
    serialize: (s) => ({ status: s.status, q: s.q, sort: s.sort }),
    applyParams: () => {}, // restored via initialViewState + key remount
  },

  "reimbursement-status": {
    id: "reimbursement-status",
    label: "Funds Request Status Report",
    category: REPORT_CATEGORY.OPERATIONAL,
    description: "Pay-to-be-paid status by Insured (legacy KM cross-claim report).",
    capability: null,
    pinnable: true, savesViews: true, exportable: true,
    primaryModeKey: null,
    defaultViewState: { statusFilter: "Open", lossRunInsuredId: "", reimbUwId: "",
                        lossRunPolicyFrom: "", lossRunPolicyTo: "", sort: { key: "claimant_name", dir: "asc" } },
    navLabel: (vs) => (vs?.lossRunInsuredId || vs?.reimbUwId) ? "Funds Request Status — Filtered" : "Funds Request Status",
    serialize: (s) => ({ statusFilter: s.statusFilter, lossRunInsuredId: s.lossRunInsuredId,
      reimbUwId: s.reimbUwId, lossRunPolicyFrom: s.lossRunPolicyFrom, lossRunPolicyTo: s.lossRunPolicyTo, sort: s.sort }),
    applyParams: (p, set) => {
      if (p.statusFilter != null) set.setStatusFilter(p.statusFilter);
      if (p.lossRunInsuredId != null) set.setLossRunInsuredId(p.lossRunInsuredId);
      if (p.reimbUwId != null) set.setReimbUwId(p.reimbUwId);
      if (p.lossRunPolicyFrom != null) set.setLossRunPolicyFrom(p.lossRunPolicyFrom);
      if (p.lossRunPolicyTo != null) set.setLossRunPolicyTo(p.lossRunPolicyTo);
      if (p.sort) set.setSort(p.sort);
    },
  },

  "open-closed": {
    id: "open-closed",
    label: "Open / Closed Claims",
    category: REPORT_CATEGORY.OPERATIONAL,
    description: "Your open/closed claims with the canonical reserve trio.",
    capability: null,
    pinnable: true, savesViews: true, exportable: false,
    primaryModeKey: "roleFilter",
    defaultViewState: { statusFilter: "Open", roleFilter: "all", viewAsUserId: null,
                        dateFrom: "", dateTo: "", sort: { key: "claim_number", dir: "asc" } },
    navLabel: (vs) => `Open / Closed — ${({ all: "All Roles", lead: "I Lead", staff: "I Contribute" }[vs?.roleFilter] || "All Roles")}`,
    serialize: (s) => ({ statusFilter: s.statusFilter, roleFilter: s.roleFilter,
      viewAsUserId: s.viewAsUserId ?? null, dateFrom: s.dateFrom, dateTo: s.dateTo, sort: s.sort }),
    applyParams: (p, set) => {
      if (p.statusFilter != null) set.setStatusFilter(p.statusFilter);
      if (p.roleFilter != null) set.setRoleFilter(p.roleFilter);
      if (p.viewAsUserId !== undefined) set.setViewAsUserId(p.viewAsUserId);
      if (p.dateFrom != null) set.setDateFrom(p.dateFrom);
      if (p.dateTo != null) set.setDateTo(p.dateTo);
      if (p.sort) set.setSort(p.sort);
    },
  },

  "advanced-claim-search": {
    id: "advanced-claim-search",
    label: "Advanced Claim Search",
    category: REPORT_CATEGORY.OPERATIONAL,
    description: "Multi-axis claim search across the book.",
    capability: null,
    pinnable: true, savesViews: true, exportable: false,
    primaryModeKey: null,
    defaultViewState: { acsFilters: null, sort: { key: "date_opened", dir: "desc" } },
    navLabel: () => "Advanced Search",
    serialize: (s) => ({ acsFilters: s.acsFilters, sort: s.sort }),
    applyParams: (p, set) => {
      if (p.acsFilters) set.setAcsFilters(p.acsFilters);
      if (p.sort) set.setSort(p.sort);
    },
  },

  // ── Financial ────────────────────────────────────────────────────────────
  "loss-run": {
    id: "loss-run",
    label: "Loss Run Report",
    category: REPORT_CATEGORY.FINANCIAL,
    description: "Cross-claim reserve & payment loss run, grouped by Insured.",
    capability: null,
    pinnable: true, savesViews: true, exportable: true,
    primaryModeKey: null,
    defaultViewState: { statusFilter: "Open", lossRunInsuredIds: [], lossRunUnderwriterIds: [],
                        lossRunPolicyId: "", lossRunVesselId: "",
                        lossRunCategory: "", lossRunPolicyFrom: "", lossRunPolicyTo: "",
                        sort: { key: "claim_number", dir: "asc" } },
    navLabel: (vs) => (vs?.lossRunInsuredIds?.length || vs?.lossRunUnderwriterIds?.length || vs?.lossRunPolicyId || vs?.lossRunVesselId || vs?.lossRunCategory) ? "Loss Run — Filtered" : "Loss Run",
    serialize: (s) => ({ statusFilter: s.statusFilter, lossRunInsuredIds: s.lossRunInsuredIds,
      lossRunUnderwriterIds: s.lossRunUnderwriterIds, lossRunPolicyId: s.lossRunPolicyId,
      lossRunVesselId: s.lossRunVesselId, lossRunCategory: s.lossRunCategory,
      lossRunPolicyFrom: s.lossRunPolicyFrom, lossRunPolicyTo: s.lossRunPolicyTo, sort: s.sort }),
    applyParams: (p, set) => {
      if (p.statusFilter != null) set.setStatusFilter(p.statusFilter);
      // RP-LOSSRUN-MULTI (2026-07-10): Insured filter is now an array.  Saved
      // views from before this change carry the single lossRunInsuredId —
      // migrate it into a one-element array on restore.
      if (p.lossRunInsuredIds != null) set.setLossRunInsuredIds((p.lossRunInsuredIds || []).map(String));
      else if (p.lossRunInsuredId) set.setLossRunInsuredIds([String(p.lossRunInsuredId)]);
      if (p.lossRunUnderwriterIds != null) set.setLossRunUnderwriterIds((p.lossRunUnderwriterIds || []).map(String));
      if (p.lossRunPolicyId != null) set.setLossRunPolicyId(p.lossRunPolicyId);
      if (p.lossRunVesselId != null) set.setLossRunVesselId(p.lossRunVesselId);
      if (p.lossRunCategory != null) set.setLossRunCategory(p.lossRunCategory);
      if (p.lossRunPolicyFrom != null) set.setLossRunPolicyFrom(p.lossRunPolicyFrom);
      if (p.lossRunPolicyTo != null) set.setLossRunPolicyTo(p.lossRunPolicyTo);
      if (p.sort) set.setSort(p.sort);
    },
  },

  "claim-payments": {
    id: "claim-payments",
    label: "Claim Payment History",
    category: REPORT_CATEGORY.FINANCIAL,
    description: "Native payment ledger across your claims.",
    capability: null,
    pinnable: true, savesViews: true, exportable: false,
    primaryModeKey: null,
    defaultViewState: { phFilters: null, sort: { key: "payment_date", dir: "desc" } },
    navLabel: () => "Payment History",
    serialize: (s) => ({ phFilters: s.phFilters, sort: s.sort }),
    applyParams: (p, set) => {
      if (p.phFilters) set.setPhFilters(p.phFilters);
      if (p.sort) set.setSort(p.sort);
    },
  },

  "production-by-party": {
    id: "production-by-party",
    label: "Production by Party",
    category: REPORT_CATEGORY.FINANCIAL,
    description: "Billable production aggregated by Insured / Underwriter / Adjuster.",
    capability: "view_invoice_financials",
    pinnable: true, savesViews: true, exportable: false,
    primaryModeKey: "dimension",
    selfContained: true,
    defaultViewState: { dimension: "insured", groupBy: ["party"], dateFrom: "", dateTo: "",
                        nameFilter: "", sortKey: "total", sortDir: "desc" },
    navLabel: (vs) => `Production by Party — ${cap1(vs?.dimension) || "Insured"}`,
    serialize: (s) => ({ dimension: s.dimension, groupBy: s.groupBy, dateFrom: s.dateFrom,
      dateTo: s.dateTo, nameFilter: s.nameFilter, sortKey: s.sortKey, sortDir: s.sortDir }),
    applyParams: () => {},
  },

  // ── Management ───────────────────────────────────────────────────────────
  productivity: {
    id: "productivity",
    label: "Productivity",
    category: REPORT_CATEGORY.MANAGEMENT,
    description: "Time & billable productivity per adjuster.",
    capability: null, // gate on mode!=self enforced server-side
    pinnable: true, savesViews: true, exportable: false,
    primaryModeKey: "mode",
    selfContained: true,
    defaultViewState: { mode: "self", userId: null, startDate: null, endDate: null, claimType: "" },
    navLabel: (vs) => `Productivity — ${PROD_MODE_LABEL[vs?.mode] || "Me"}`,
    serialize: (s) => ({ mode: s.mode, userId: s.userId ?? null, startDate: s.startDate,
      endDate: s.endDate, claimType: s.claimType }),
    applyParams: () => {},
  },

  // ── Market / Regulatory ──────────────────────────────────────────────────
  bordereau: {
    id: "bordereau",
    label: "Bordereau (BDX)",
    category: REPORT_CATEGORY.MARKET,
    description: "Lloyd's / underwriter bordereau placement & exception runs.",
    capability: "view_bordereau_reports",
    pinnable: true, savesViews: true, exportable: false,
    primaryModeKey: "variant",
    selfContained: true,
    defaultViewState: { profileId: null, variant: "standard", start: null, end: null, tab: "preview" },
    navLabel: (vs) => `Bordereau — ${cap1(vs?.variant) || "Standard"}`,
    serialize: (s) => ({ profileId: s.profileId ?? null, variant: s.variant, start: s.start,
      end: s.end, tab: s.tab }),
    applyParams: () => {},
  },
};

// Left-rail order (within categories the array order wins).
export const REPORT_ORDER = [
  "adjuster-wip", "reimbursement-requests", "reimbursement-status", "open-closed", "advanced-claim-search",
  "loss-run", "claim-payments", "production-by-party",
  "productivity",
  "bordereau",
];

export const CATEGORY_ORDER = [
  REPORT_CATEGORY.OPERATIONAL, REPORT_CATEGORY.FINANCIAL,
  REPORT_CATEGORY.MANAGEMENT, REPORT_CATEGORY.MARKET,
];

// ── Identity helpers (single source of truth for pin ref_id / labels) ──
export function getReport(reportId) { return REPORTS[reportId] || null; }

export function primaryMode(reportId, viewState) {
  const r = REPORTS[reportId];
  return r?.primaryModeKey ? (viewState?.[r.primaryModeKey] ?? null) : null;
}

// ref_id for a granular report-view pin/recent. Reports with a primary mode get
// `${id}:${mode}` (so distinct modes are distinct pins, a filter tweak upserts
// the same pin); mode-less reports fall back to the report id.
export function reportViewRefId(reportId, viewState) {
  const m = primaryMode(reportId, viewState);
  return m != null && m !== "" ? `${reportId}:${m}` : reportId;
}

export function reportNavLabel(reportId, viewState) {
  const r = REPORTS[reportId];
  return r ? r.navLabel(viewState || r.defaultViewState) : reportId;
}

// Is the base report visible to this user? (capability gate)
export function reportVisible(reportId) {
  const r = REPORTS[reportId];
  if (!r) return false;
  return !r.capability || can(r.capability);
}

// Is a specific mode of a report permitted? (e.g. WIP rollup needs team-reports)
export function modeVisible(reportId, mode) {
  const r = REPORTS[reportId];
  if (!r || !r.modeCapability || mode == null) return true;
  const reqCap = r.modeCapability[mode];
  return !reqCap || can(reqCap);
}

// Stable signature of a view; used by the bridge to detect change without loops.
export function viewSignature(reportId, params) {
  return (reportId || "") + "|" + JSON.stringify(params || {});
}

export function reportsByCategory() {
  const out = {};
  for (const cat of CATEGORY_ORDER) out[cat] = [];
  for (const id of REPORT_ORDER) {
    const r = REPORTS[id];
    if (r && out[r.category]) out[r.category].push(r);
  }
  return out;
}
